import { useEffect } from "react";
import { Button } from "../components/Button";
import { DataTable, type Column } from "../components/DataTable";
import { IdChip } from "../components/IdChip";
import { StatusPill, type StatusCategory } from "../components/StatusPill";
import { encodeGlobalId } from "../lib/globalId";
import { langFor } from "../lib/thai";

/**
 * Developer specimen of the shared components. NOT a product screen and not in the nav.
 * All data is synthetic. Status pills show categories only: real status names wait for SA-02.
 */

interface SampleRow {
  key: string;
  globalId: string;
  summary: string;
  category: StatusCategory;
  estimateMd: number;
}

const summaries = [
  "Export shift report to Excel with depot filter",
  "แก้ไขปัญหาการคำนวณระยะทางไม่ตรงกับรายงานประจำวัน",
  "Login screen keeps the typed user name after a failed attempt",
  "เพิ่มตัวกรองวันที่ในหน้ารายงานสรุป",
];
const categories: StatusCategory[] = ["todo", "in_progress", "done"];
const categoryLabel: Record<StatusCategory, string> = { todo: "To do", in_progress: "In progress", done: "Done" };

const rows: SampleRow[] = Array.from({ length: 40 }, (_, index) => ({
  key: `DEMO-${index + 1}`,
  globalId: encodeGlobalId(BigInt(1000 + index)),
  summary: summaries[index % summaries.length] ?? "",
  category: categories[index % categories.length] ?? "todo",
  estimateMd: ((index % 8) + 1) * 0.25,
}));

const columns: readonly Column<SampleRow>[] = [
  { key: "key", header: "Key", cell: (row) => <span className="gf-key">{row.key}</span> },
  { key: "gid", header: "Global ID", cell: (row) => <span className="gf-key">{row.globalId}</span> },
  { key: "summary", header: "Summary", cell: (row) => <span lang={langFor(row.summary)}>{row.summary}</span>, wrap: true },
  {
    key: "status",
    header: "Category",
    cell: (row) => <StatusPill name={categoryLabel[row.category]} category={row.category} />,
  },
  { key: "md", header: "Est. MD", numeric: true, cell: (row) => row.estimateMd.toFixed(2) },
];

export function SpecimenPage() {
  useEffect(() => {
    document.title = "Component specimen · Giraffe";
  }, []);

  return (
    <>
      <div className="gf-pagehead">
        <h1>Component specimen</h1>
        <p className="sub">Developer reference for shared components · synthetic data · not a product screen</p>
      </div>

      <section className="gf-section" aria-labelledby="spec-buttons">
        <div className="sechd">
          <h2 id="spec-buttons">Buttons</h2>
          <span className="secs">primary, secondary, danger (irreversible only), disabled</span>
        </div>
        <div className="pad row">
          <Button>Create work item</Button>
          <Button variant="secondary">Cancel</Button>
          <Button variant="danger">Delete</Button>
          <Button disabled>Start sprint</Button>
        </div>
      </section>

      <section className="gf-section" aria-labelledby="spec-identity">
        <div className="sechd">
          <h2 id="spec-identity">Identity and status</h2>
          <span className="secs">copyable key and global ID; pill colour from the status category</span>
        </div>
        <div className="pad row">
          <IdChip itemKey="PATPMS-216" globalId={encodeGlobalId(335459n)} />
        </div>
        <div className="pad row" style={{ marginTop: 12 }}>
          <StatusPill name="To do" category="todo" />
          <StatusPill name="In progress" category="in_progress" />
          <StatusPill name="Done" category="done" />
          <StatusPill name="กำลังดำเนินการ" category="in_progress" />
        </div>
      </section>

      <section className="gf-section" aria-labelledby="spec-table">
        <div className="sechd">
          <h2 id="spec-table">Table</h2>
          <span className="secs">.tablewrap.tall with table.compact: sticky header and key column</span>
        </div>
        <DataTable caption="Synthetic work items" columns={columns} rows={rows} rowKey={(row) => row.key} tall />
      </section>
    </>
  );
}
